import React from 'react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { 
  Target, 
  Loader2, 
  Pencil, 
  X, 
  Check,
  Plus
} from 'lucide-react';

// Goals Editor Component
function GoalsEditor({ goals, onUpdate }) {
  const addGoal = () => {
    onUpdate([...goals, { goal: '', priority: 'MEDIUM' }]);
  };
  
  const updateGoal = (index, field, value) => {
    const newGoals = [...goals];
    newGoals[index] = { ...newGoals[index], [field]: value };
    onUpdate(newGoals);
  };
  
  const removeGoal = (index) => {
    onUpdate(goals.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-2">
      {goals.map((goal, i) => (
        <div key={i} className="flex items-start gap-2">
          <select
            value={goal.priority || 'MEDIUM'}
            onChange={(e) => updateGoal(i, 'priority', e.target.value)}
            className="h-10 rounded-md border border-input bg-background px-2 text-xs"
          >
            <option value="HIGH">HIGH</option>
            <option value="MEDIUM">MEDIUM</option>
            <option value="LOW">LOW</option>
          </select>
          <Input
            value={goal.goal || ''}
            onChange={(e) => updateGoal(i, 'goal', e.target.value)}
            className="flex-1"
          />
          <Button variant="ghost" size="sm" onClick={() => removeGoal(i)}>
            <X className="w-4 h-4" />
          </Button>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={addGoal}>
        <Plus className="w-4 h-4 mr-1" /> Add Goal
      </Button>
    </div>
  );
}

// Goal Item Display
function GoalItem({ goal }) {
  const priority = goal.priority || 'MEDIUM';
  const colorClass = priority === 'HIGH' ? 'bg-red-100 text-red-700' : 
                     priority === 'MEDIUM' ? 'bg-yellow-100 text-yellow-700' : 
                     'bg-green-100 text-green-700';
  return (
    <div className="flex items-start gap-3">
      <span className={`text-xs font-semibold px-2 py-1 rounded ${colorClass}`}>{priority}</span>
      <span className="text-foreground">{goal.goal || goal}</span>
    </div>
  );
}

export default function WebsiteAnalysisSection({ 
  websiteContext, 
  editedContext, 
  setEditedContext, 
  editingAnalysis, 
  setEditingAnalysis, 
  handleSave, 
  handleCancelEdit, 
  saving 
}) {
  if (!websiteContext) return null;
  
  const context = editedContext || websiteContext;
  // Handle both primary_goals and business_goals for backwards compatibility
  const goals = context.primary_goals || context.business_goals || [];
  const ctas = context.ctas || context.primary_ctas || [];
  const products = context.products_services || [];

  return (
    <div className="bg-surface border border-border rounded-2xl p-6 mb-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
            <Target className="w-5 h-5 text-blue-500" />
          </div>
          <div>
            <h2 className="font-semibold text-foreground">Website Analysis</h2>
            <p className="text-sm text-muted-foreground">Extracted from your site</p>
          </div>
        </div>
        {!editingAnalysis ? (
          <Button onClick={() => setEditingAnalysis(true)} variant="outline" size="sm" className="rounded-full" data-testid="edit-analysis-btn">
            <Pencil className="w-4 h-4 mr-2" />
            Edit
          </Button>
        ) : (
          <div className="flex items-center gap-2">
            <Button onClick={handleCancelEdit} variant="outline" size="sm" className="rounded-full">
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} size="sm" className="rounded-full bg-primary text-white">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Save
            </Button>
          </div>
        )}
      </div>

      <div className="space-y-6">
        {/* About */}
        <div>
          <label className="text-xs font-semibold uppercase text-muted-foreground mb-2 block">About</label>
          {editingAnalysis ? (
            <Textarea
              value={context.website_description || ''}
              onChange={(e) => setEditedContext({ ...context, website_description: e.target.value })}
              className="min-h-[80px]"
            />
          ) : (
            <p className="text-foreground">{context.website_description || 'No description available'}</p>
          )}
        </div>

        {/* CTAs */}
        {ctas.length > 0 && (
          <div>
            <label className="text-xs font-semibold uppercase text-muted-foreground mb-2 block">CTAs (Button Text)</label>
            {editingAnalysis ? (
              <Input
                value={ctas.map(c => c.text || c).join(', ')}
                onChange={(e) => {
                  const newCtas = e.target.value.split(',').map(s => ({ text: s.trim() })).filter(c => c.text);
                  setEditedContext({ ...context, ctas: newCtas, primary_ctas: newCtas });
                }}
                placeholder="e.g., Get Started, Learn More"
              />
            ) : (
              <div className="flex flex-wrap gap-2">
                {ctas.map((cta, i) => (
                  <span key={i} className="text-sm bg-muted text-muted-foreground px-3 py-1.5 rounded-lg">
                    {cta.text || cta}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Business Goals - Always show, even if empty */}
        <div>
          <label className="text-xs font-semibold uppercase text-muted-foreground mb-2 block">Business Goals</label>
          {editingAnalysis ? (
            <GoalsEditor 
              goals={context.primary_goals || context.business_goals || []} 
              onUpdate={(newGoals) => setEditedContext({ ...context, primary_goals: newGoals, business_goals: undefined })}
            />
          ) : goals.length > 0 ? (
            <div className="space-y-2">
              {goals.map((goal, i) => (
                <GoalItem key={i} goal={goal} />
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground italic">No business goals detected. Click Edit to add goals.</p>
          )}
        </div>

        {/* Products */}
        {products.length > 0 && (
          <div>
            <label className="text-xs font-semibold uppercase text-muted-foreground mb-2 block">Products/Services</label>
            {editingAnalysis ? (
              <Input
                value={products.map(p => p.name || p).join(', ')}
                onChange={(e) => {
                  const newProducts = e.target.value.split(',').map(s => ({ name: s.trim() })).filter(p => p.name);
                  setEditedContext({ ...context, products_services: newProducts });
                }}
                placeholder="e.g., Product A, Service B"
              />
            ) : (
              <div className="flex flex-wrap gap-2">
                {products.map((product, i) => (
                  <span key={i} className="text-sm bg-primary/10 text-primary px-3 py-1.5 rounded-lg">
                    {product.name || product}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      
      <p className="text-xs text-muted-foreground italic mt-6">
        Business goals will be used as fallback if you don't specify a goal in your campaign context below.
      </p>
    </div>
  );
}
